import { transformSitesToOptions } from '@/components/Assets/helpers/transformOptions';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import SiteModal from '@/pages/Assets/SiteModal';
import {
  useGetOrganizationSitesQuery,
  useGetSiteByIdQuery,
} from '@/redux/slices/assetApiSlice';
import {
  Button,
  Flex,
  FlexProps,
  SelectField,
  useBreakpoint,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import { formatAddress } from 'common/utils/formatAddress';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  filterOutInternationalSites,
  isCaOrPrSite,
} from '@/helpers/isInternationalSite';

type SiteGroupFieldProps = {
  name: string;
  label?: string;
  id?: string;
  buttonId?: string;
  placeholder?: string;
  required?: boolean;
  shouldShowAddress?: boolean;
  isInternationalFlow?: boolean;
} & FlexProps;

const SiteGroupField = ({
  name,
  label,
  id = 'site-select',
  buttonId = 'button-add-site',
  placeholder,
  required,
  shouldShowAddress,
  isInternationalFlow = false,
  ...containerProps
}: SiteGroupFieldProps) => {
  const { t } = useTranslation('common');
  const { sm } = useBreakpoint();
  const { openModal, closeModal } = useGlobalUI();
  const organizationId = useGetOrganizationId();
  const { sites, isFetchingOrganizationSites } = useGetOrganizationSitesQuery(
    { organizationId },
    {
      selectFromResult: ({
        data: sites,
        isFetching: isFetchingOrganizationSites,
      }) => {
        return {
          // Exclude global sites. The international flow can only ship to
          // CA/PR sites, the default flow only to US sites.
          sites: sites
            ? isInternationalFlow
              ? sites.filter(site => site.organizationId && isCaOrPrSite(site))
              : filterOutInternationalSites(
                  sites.filter(site => site.organizationId)
                )
            : [],
          isFetchingOrganizationSites,
        };
      },
    }
  );
  const siteOptions = useMemo(
    () => transformSitesToOptions(sites || []),
    [sites]
  );
  const { control } = useFormContext();
  const currentSiteOption = useWatch({ name });
  const { data: site } = useGetSiteByIdQuery(
    {
      siteId: currentSiteOption?.value,
      organizationId: organizationId,
    },
    { skip: !currentSiteOption?.value }
  );
  const formattedAddress = formatAddress(site?.address);

  return (
    <Flex
      columnGap="2rem"
      width="100%"
      vertical={!sm}
      hAlignContent={sm ? 'between' : 'center'}
      {...containerProps}>
      <SelectField
        id={id}
        name={name}
        label={label || t('offboardings.siteGroupField.siteLabel')}
        control={control}
        placeholder={
          placeholder || t('offboardings.siteGroupField.sitePlaceholder')
        }
        options={siteOptions}
        isLoading={isFetchingOrganizationSites}
        disabled={isFetchingOrganizationSites}
        required={required}
        helperText={shouldShowAddress ? formattedAddress : ''}
        isSearchable
        isClearable
      />
      <Button
        id={buttonId}
        iconBefore="add-circle"
        onClick={() => openModal(<SiteModal hideModal={() => closeModal()} />)}
        variant="outline"
        mt={sm ? '2rem' : 0}
        mb={sm ? 0 : '2rem'}
        width="14.5rem">
        {t('offboardings.siteGroupField.addSiteButton')}
      </Button>
    </Flex>
  );
};

export default SiteGroupField;
